import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import adminAxios from '../adminAxios';
import { formatLockDateTime, formatDuration, formatAgeLabel } from './utils';
import type {
  AnalysisBlockedBooksInspectorResponse,
  AnalysisBlockedBulkExpireResponse,
  AnalysisBlockedLockInspectorResponse,
  CharacterAnalysisResetBookResponse,
  InconsistentLockInspectorResponse,
  InconsistentLockRepairResponse,
  LockExpireResponse,
  LockInspectorLock,
  LockInspectorResponse,
  LockInspectorSummary,
} from './types';

const { Option } = Select;
const { Text } = Typography;

const STATUS_OPTIONS = [
  'pending',
  'retrying',
  'completed',
  'failed',
  'error',
  'expired',
  'released',
  'cancelled',
  'canceled',
  'aborted',
] as const;

const TYPE_OPTIONS = ['translation', 'proofread', 'audiobookgeneration', 'editingnotes'] as const;

const QUERY_LABELS = {
  inspector: 'Inspector',
  inconsistent: 'Inconsistent locks',
  analysisBlocked: 'Analysis-blocked locks',
  analysisBlockedBooks: 'Stuck books (blocked manifests)',
} as const;

const statusColorMap: Record<string, string> = {
  pending: 'default',
  retrying: 'orange',
  completed: 'green',
  failed: 'red',
  error: 'magenta',
  expired: 'gold',
  released: 'purple',
  cancelled: 'volcano',
  canceled: 'volcano',
  aborted: 'volcano',
};

const toSentence = (value?: string) => {
  if (!value) return '—';
  const normalized = value.replace(/[_-]/g, ' ').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const humanizeCount = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  return value.toLocaleString();
};

const formatFilters = (summary?: LockInspectorSummary | null) => {
  if (!summary) return null;
  const statuses = Object.entries(summary.statusCounts || {}).map(
    ([status, count]) => `${toSentence(status)}: ${count.toLocaleString()}`
  );
  const types = Object.entries(summary.typeCounts || {}).map(
    ([type, count]) => `${toSentence(type)}: ${count.toLocaleString()}`
  );
  return { statuses, types };
};

interface LockInspectorProps {
  apiBase: string;
}

const LockInspector: React.FC<LockInspectorProps> = ({ apiBase }) => {
  const { token } = theme.useToken();
  const [userId, setUserId] = useState('');
  const [bookId, setBookId] = useState('');
  const [chapterGuid, setChapterGuid] = useState('');
  const [lockGuid, setLockGuid] = useState('');
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [activeOnly, setActiveOnly] = useState(true);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [limit, setLimit] = useState(50);
  const [locks, setLocks] = useState<LockInspectorLock[]>([]);
  const [summary, setSummary] = useState<LockInspectorSummary | null>(null);
  const [responseMeta, setResponseMeta] = useState<{
    inspectedAt: string;
    totalMatched: number;
    truncated: boolean;
    queryLabel: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorModalLock, setErrorModalLock] = useState<LockInspectorLock | null>(null);
  const [expireModalLock, setExpireModalLock] = useState<LockInspectorLock | null>(null);
  const [expireReason, setExpireReason] = useState('Expired via admin dashboard');
  const [expireLoading, setExpireLoading] = useState(false);
  const [inconsistentMinAgeMinutes, setInconsistentMinAgeMinutes] = useState(5);
  const [inconsistentBulkModalVisible, setInconsistentBulkModalVisible] = useState(false);
  const [inconsistentBulkReason, setInconsistentBulkReason] = useState(
    'Repair terminal status/processingState mismatch'
  );
  const [inconsistentBulkMaxLocks, setInconsistentBulkMaxLocks] = useState(200);
  const [inconsistentBulkLoading, setInconsistentBulkLoading] = useState(false);
  const [analysisBlockedBulkModalVisible, setAnalysisBlockedBulkModalVisible] = useState(false);
  const [analysisBlockedBulkReason, setAnalysisBlockedBulkReason] = useState(
    'Expired via admin bulk action for character-analysis blocked locks'
  );
  const [analysisBlockedBulkMaxLocks, setAnalysisBlockedBulkMaxLocks] = useState(200);
  const [analysisBlockedBulkLoading, setAnalysisBlockedBulkLoading] = useState(false);
  const [resetBookModalVisible, setResetBookModalVisible] = useState(false);
  const [resetBookReason, setResetBookReason] = useState('Hard reset stuck character-analysis run');
  const [resetBookForce, setResetBookForce] = useState(false);
  const [resetBookFreeRunFlag, setResetBookFreeRunFlag] = useState(false);
  const [resetBookDryRunLoading, setResetBookDryRunLoading] = useState(false);
  const [resetBookExecuteLoading, setResetBookExecuteLoading] = useState(false);
  const [resetBookPreview, setResetBookPreview] = useState<CharacterAnalysisResetBookResponse | null>(null);
  const [resetBookResult, setResetBookResult] = useState<CharacterAnalysisResetBookResponse | null>(null);
  const [analysisBlockedBooks, setAnalysisBlockedBooks] = useState<
    AnalysisBlockedBooksInspectorResponse['books']
  >([]);
  const filteredLocks = useMemo(() => {
    const statusSet = new Set(statusFilters.map((s) => s.toLowerCase()));
    const typeSet = new Set(typeFilters.map((t) => t.toLowerCase()));
    return locks.filter((lock) => {
      if (statusSet.size && !statusSet.has((lock.statusNormalized || lock.status || '').toLowerCase())) {
        return false;
      }
      if (typeSet.size && !typeSet.has((lock.typeNormalized || (lock.type as string) || '').toLowerCase())) {
        return false;
      }
      if (activeOnly && lock.isActive === false) {
        return false;
      }
      if (errorsOnly && !lock.hasError) {
        return false;
      }
      return true;
    });
  }, [activeOnly, errorsOnly, locks, statusFilters, typeFilters]);

  const analysisBlockedBookIds = useMemo(
    () => analysisBlockedBooks.map((entry) => entry.bookId).filter(Boolean),
    [analysisBlockedBooks]
  );

  const hasIdentifier = useMemo(
    () => [userId, bookId, chapterGuid, lockGuid].some((value) => value.trim().length > 0),
    [userId, bookId, chapterGuid, lockGuid]
  );

  const resetForm = useCallback(() => {
    setUserId('');
    setBookId('');
    setChapterGuid('');
    setLockGuid('');
    setStatusFilters([]);
    setTypeFilters([]);
    setActiveOnly(true);
    setErrorsOnly(false);
    setSortOrder('newest');
    setLimit(50);
    setInconsistentMinAgeMinutes(5);
    setInconsistentBulkMaxLocks(200);
    setInconsistentBulkReason('Repair terminal status/processingState mismatch');
    setInconsistentBulkModalVisible(false);
    setAnalysisBlockedBulkMaxLocks(200);
    setAnalysisBlockedBulkReason('Expired via admin bulk action for character-analysis blocked locks');
    setAnalysisBlockedBulkModalVisible(false);
    setResetBookModalVisible(false);
    setResetBookReason('Hard reset stuck character-analysis run');
    setResetBookForce(false);
    setResetBookFreeRunFlag(false);
    setResetBookPreview(null);
    setResetBookResult(null);
    setAnalysisBlockedBooks([]);
    setLocks([]);
    setSummary(null);
    setResponseMeta(null);
  }, []);

  const buildScopeParams = useCallback(() => {
    const trimmedLockInput = lockGuid.trim();
    const upperLockInput = trimmedLockInput.toUpperCase();
    const isSupportCode = /^RC-[A-Z0-9]{3,}$/.test(upperLockInput);
    return {
      user_id: userId.trim() || undefined,
      book_id: bookId.trim() || undefined,
      chapter_guid: chapterGuid.trim() || undefined,
      lock_guid: isSupportCode ? undefined : trimmedLockInput || undefined,
      support_code: isSupportCode ? upperLockInput : undefined,
    };
  }, [bookId, chapterGuid, lockGuid, userId]);

  const inspectLocks = useCallback(async () => {
    if (!hasIdentifier) {
      message.warning('Enter at least one of user ID, book ID, chapter GUID, or lock GUID.');
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, any> = {
        ...buildScopeParams(),
        limit,
        sort: sortOrder,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await adminAxios.get<LockInspectorResponse>(
        `${apiBase}/admin/lock-inspector`,
        { params }
      );
      setAnalysisBlockedBooks([]);
      setLocks(response.data.locks || []);
      setSummary(response.data.summary || null);
      setResponseMeta({
        inspectedAt: response.data.inspectedAt,
        totalMatched: response.data.totalMatched,
        truncated: response.data.truncated,
        queryLabel: QUERY_LABELS.inspector,
      });
      if (response.data.truncated) {
        message.warning('Results truncated to the requested limit.');
      } else {
        message.success(`Loaded ${response.data.locks.length || 0} lock(s).`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load locks';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, [apiBase, buildScopeParams, hasIdentifier, limit, sortOrder]);

  const inspectInconsistentLocks = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        ...buildScopeParams(),
        limit,
        sort: sortOrder,
        min_age_minutes: inconsistentMinAgeMinutes,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await adminAxios.get<InconsistentLockInspectorResponse>(
        `${apiBase}/admin/lock-inspector/inconsistent`,
        { params }
      );
      setAnalysisBlockedBooks([]);
      setLocks(response.data.locks || []);
      setSummary(response.data.summary || null);
      setResponseMeta({
        inspectedAt: response.data.inspectedAt,
        totalMatched: response.data.totalMatched,
        truncated: response.data.truncated,
        queryLabel: QUERY_LABELS.inconsistent,
      });
      if (response.data.truncated) {
        message.warning('Results truncated to the requested limit.');
      } else {
        message.success(`Loaded ${response.data.locks.length || 0} inconsistent lock(s).`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load inconsistent locks';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, [apiBase, buildScopeParams, inconsistentMinAgeMinutes, limit, sortOrder]);

  const inspectAnalysisBlockedLocks = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        ...buildScopeParams(),
        limit,
        sort: sortOrder,
        min_age_minutes: inconsistentMinAgeMinutes,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await adminAxios.get<AnalysisBlockedLockInspectorResponse>(
        `${apiBase}/admin/lock-inspector/analysis-blocked`,
        { params }
      );
      setAnalysisBlockedBooks([]);
      setLocks(response.data.locks || []);
      setSummary(response.data.summary || null);
      setResponseMeta({
        inspectedAt: response.data.inspectedAt,
        totalMatched: response.data.totalMatched,
        truncated: response.data.truncated,
        queryLabel: QUERY_LABELS.analysisBlocked,
      });
      if (response.data.truncated) {
        message.warning('Results truncated to the requested limit.');
      } else {
        message.success(`Loaded ${response.data.locks.length || 0} analysis-blocked lock(s).`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load analysis-blocked locks';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, [apiBase, buildScopeParams, inconsistentMinAgeMinutes, limit, sortOrder]);

  const inspectAnalysisBlockedBooks = useCallback(async () => {
    setLoading(true);
    try {
      const scope = buildScopeParams();
      const params: Record<string, any> = {
        user_id: scope.user_id,
        book_id: scope.book_id,
        chapter_guid: scope.chapter_guid,
        limit,
        min_age_minutes: inconsistentMinAgeMinutes,
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });
      const response = await adminAxios.get<AnalysisBlockedBooksInspectorResponse>(
        `${apiBase}/admin/lock-inspector/analysis-blocked-books`,
        { params }
      );
      setLocks([]);
      setSummary(null);
      setAnalysisBlockedBooks(response.data.books || []);
      setResponseMeta({
        inspectedAt: response.data.inspectedAt,
        totalMatched: response.data.totalBooks || 0,
        truncated: response.data.truncated,
        queryLabel: QUERY_LABELS.analysisBlockedBooks,
      });
      if (response.data.truncated) {
        message.warning('Results truncated to the requested limit.');
      } else {
        message.success(`Loaded ${response.data.totalBooks || 0} stuck book(s).`);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to load stuck books';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setLoading(false);
    }
  }, [apiBase, buildScopeParams, inconsistentMinAgeMinutes, limit]);

  const closeErrorModal = useCallback(() => {
    setErrorModalLock(null);
  }, []);

  const openExpireModal = useCallback((lock: LockInspectorLock) => {
    setExpireModalLock(lock);
    setExpireReason('Expired via admin dashboard');
  }, []);

  const closeExpireModal = useCallback(() => {
    setExpireModalLock(null);
    setExpireReason('Expired via admin dashboard');
  }, []);

  const handleExpireLock = useCallback(async () => {
    if (!expireModalLock) {
      return;
    }
    const payload = {
      document_id: expireModalLock.documentId,
      lock_guid: expireModalLock.lockGuid,
      reason: expireReason.trim() || undefined,
    };
    setExpireLoading(true);
    try {
      const response = await adminAxios.post<LockExpireResponse>(
        `${apiBase}/admin/lock-expire`,
        payload
      );
      message.success('Lock expired and credits released when applicable.');
      closeExpireModal();
      if (responseMeta?.queryLabel === QUERY_LABELS.inconsistent) {
        await inspectInconsistentLocks();
      } else if (responseMeta?.queryLabel === QUERY_LABELS.analysisBlocked) {
        await inspectAnalysisBlockedLocks();
      } else if (hasIdentifier) {
        await inspectLocks();
      }
      if (errorModalLock && errorModalLock.lockGuid === expireModalLock.lockGuid) {
        setErrorModalLock(response.data.lock);
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to expire lock';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setExpireLoading(false);
    }
  }, [
    apiBase,
    closeExpireModal,
    errorModalLock,
    expireModalLock,
    expireReason,
    hasIdentifier,
    inspectAnalysisBlockedLocks,
    inspectInconsistentLocks,
    inspectLocks,
    responseMeta?.queryLabel,
  ]);

  const canRepairInconsistent = useMemo(
    () =>
      !loading &&
      responseMeta?.queryLabel === QUERY_LABELS.inconsistent &&
      Array.isArray(locks) &&
      locks.length > 0,
    [loading, locks, responseMeta?.queryLabel]
  );

  const canExpireAnalysisBlocked = useMemo(
    () =>
      !loading &&
      responseMeta?.queryLabel === QUERY_LABELS.analysisBlocked &&
      Array.isArray(locks) &&
      locks.length > 0,
    [loading, locks, responseMeta?.queryLabel]
  );

  const openInconsistentBulkModal = useCallback(() => {
    if (!canRepairInconsistent) {
      message.warning('Run "Find inconsistent" first, then repair from those results.');
      return;
    }
    setInconsistentBulkModalVisible(true);
    setInconsistentBulkMaxLocks(Math.min(Math.max(locks.length || 1, 1), 1000));
    setInconsistentBulkReason('Repair terminal status/processingState mismatch');
  }, [canRepairInconsistent, locks.length]);

  const closeInconsistentBulkModal = useCallback(() => {
    setInconsistentBulkModalVisible(false);
  }, []);

  const openAnalysisBlockedBulkModal = useCallback(() => {
    if (!canExpireAnalysisBlocked) {
      message.warning('Run "Find analysis-blocked locks" first, then expire from those results.');
      return;
    }
    setAnalysisBlockedBulkModalVisible(true);
    setAnalysisBlockedBulkMaxLocks(Math.min(Math.max(locks.length || 1, 1), 1000));
    setAnalysisBlockedBulkReason('Expired via admin bulk action for character-analysis blocked locks');
  }, [canExpireAnalysisBlocked, locks.length]);

  const closeAnalysisBlockedBulkModal = useCallback(() => {
    setAnalysisBlockedBulkModalVisible(false);
  }, []);

  const handleRepairInconsistent = useCallback(async () => {
    if (!canRepairInconsistent) {
      message.warning('Run "Find inconsistent" first, then repair from those results.');
      return;
    }
    setInconsistentBulkLoading(true);
    try {
      const scopeParams = buildScopeParams();
      const hasScope = Boolean(
        scopeParams.user_id ||
          scopeParams.book_id ||
          scopeParams.chapter_guid ||
          scopeParams.lock_guid ||
          scopeParams.support_code
      );
      const payload = {
        ...scopeParams,
        reason: inconsistentBulkReason.trim() || undefined,
        max_locks: Math.min(Math.max(inconsistentBulkMaxLocks, 1), 1000),
        min_age_minutes: Math.max(0, inconsistentMinAgeMinutes),
        allow_global: !hasScope,
      };
      const response = await adminAxios.post<InconsistentLockRepairResponse>(
        `${apiBase}/admin/lock-repair-inconsistent`,
        payload
      );
      message.success(
        `Repaired ${response.data.repairedCount} lock(s).`
      );
      if ((response.data.failures || []).length > 0) {
        message.warning(`${response.data.failures.length} lock(s) could not be repaired.`);
      }
      closeInconsistentBulkModal();
      await inspectInconsistentLocks();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to repair inconsistent locks';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setInconsistentBulkLoading(false);
    }
  }, [
    apiBase,
    buildScopeParams,
    canRepairInconsistent,
    closeInconsistentBulkModal,
    inconsistentBulkMaxLocks,
    inconsistentBulkReason,
    inconsistentMinAgeMinutes,
    inspectInconsistentLocks,
  ]);

  const handleExpireAnalysisBlocked = useCallback(async () => {
    if (!canExpireAnalysisBlocked) {
      message.warning('Run "Find analysis-blocked locks" first, then expire from those results.');
      return;
    }
    setAnalysisBlockedBulkLoading(true);
    try {
      const scopeParams = buildScopeParams();
      const hasScope = Boolean(
        scopeParams.user_id ||
          scopeParams.book_id ||
          scopeParams.chapter_guid ||
          scopeParams.lock_guid ||
          scopeParams.support_code
      );
      const payload = {
        ...scopeParams,
        reason: analysisBlockedBulkReason.trim() || undefined,
        max_locks: Math.min(Math.max(analysisBlockedBulkMaxLocks, 1), 1000),
        min_age_minutes: Math.max(0, inconsistentMinAgeMinutes),
        allow_global: !hasScope,
        dry_run: false,
      };
      const response = await adminAxios.post<AnalysisBlockedBulkExpireResponse>(
        `${apiBase}/admin/lock-expire-analysis-blocked`,
        payload
      );
      message.success(`Expired ${response.data.expiredCount} lock(s).`);
      if ((response.data.failures || []).length > 0) {
        message.warning(`${response.data.failures.length} lock(s) could not be expired.`);
      }
      closeAnalysisBlockedBulkModal();
      await inspectAnalysisBlockedLocks();
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to expire analysis-blocked locks';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setAnalysisBlockedBulkLoading(false);
    }
  }, [
    analysisBlockedBulkMaxLocks,
    analysisBlockedBulkReason,
    apiBase,
    buildScopeParams,
    canExpireAnalysisBlocked,
    closeAnalysisBlockedBulkModal,
    inconsistentMinAgeMinutes,
    inspectAnalysisBlockedLocks,
  ]);

  const openResetBookModal = useCallback(async () => {
    const normalizedBookId = bookId.trim();
    if (!normalizedBookId) {
      message.warning('Enter a Book ID first.');
      return;
    }
    setResetBookModalVisible(true);
    setResetBookPreview(null);
    setResetBookResult(null);
    setResetBookDryRunLoading(true);
    try {
      const response = await adminAxios.post<CharacterAnalysisResetBookResponse>(
        `${apiBase}/admin/character-analysis/reset-book`,
        {
          book_id: normalizedBookId,
          reason: resetBookReason.trim() || undefined,
          dry_run: true,
          force: resetBookForce,
          reset_free_run_flag: resetBookFreeRunFlag,
        }
      );
      setResetBookPreview(response.data);
      message.success('Dry run completed.');
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to preview reset impact';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setResetBookDryRunLoading(false);
    }
  }, [apiBase, bookId, resetBookForce, resetBookFreeRunFlag, resetBookReason]);

  const closeResetBookModal = useCallback(() => {
    if (resetBookDryRunLoading || resetBookExecuteLoading) {
      return;
    }
    setResetBookModalVisible(false);
  }, [resetBookDryRunLoading, resetBookExecuteLoading]);

  const runResetBookDryRun = useCallback(async () => {
    const normalizedBookId = bookId.trim();
    if (!normalizedBookId) {
      message.warning('Enter a Book ID first.');
      return;
    }
    setResetBookDryRunLoading(true);
    try {
      const response = await adminAxios.post<CharacterAnalysisResetBookResponse>(
        `${apiBase}/admin/character-analysis/reset-book`,
        {
          book_id: normalizedBookId,
          reason: resetBookReason.trim() || undefined,
          dry_run: true,
          force: resetBookForce,
          reset_free_run_flag: resetBookFreeRunFlag,
        }
      );
      setResetBookPreview(response.data);
      setResetBookResult(null);
      message.success('Dry run refreshed.');
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to preview reset impact';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setResetBookDryRunLoading(false);
    }
  }, [apiBase, bookId, resetBookForce, resetBookFreeRunFlag, resetBookReason]);

  const executeResetBook = useCallback(async () => {
    const normalizedBookId = bookId.trim();
    if (!normalizedBookId) {
      message.warning('Enter a Book ID first.');
      return;
    }
    setResetBookExecuteLoading(true);
    try {
      const response = await adminAxios.post<CharacterAnalysisResetBookResponse>(
        `${apiBase}/admin/character-analysis/reset-book`,
        {
          book_id: normalizedBookId,
          reason: resetBookReason.trim() || undefined,
          dry_run: false,
          force: resetBookForce,
          reset_free_run_flag: resetBookFreeRunFlag,
        }
      );
      setResetBookResult(response.data);
      setResetBookPreview(response.data);
      message.success('Book recovery completed.');
      if (hasIdentifier) {
        await inspectLocks();
      }
    } catch (error: any) {
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to execute book reset';
      message.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setResetBookExecuteLoading(false);
    }
  }, [
    apiBase,
    bookId,
    hasIdentifier,
    inspectLocks,
    resetBookForce,
    resetBookFreeRunFlag,
    resetBookReason,
  ]);

  const copyToClipboard = useCallback(async (content: string) => {
    if (!content) {
      message.warning('Nothing to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(content);
      message.success('Copied to clipboard');
    } catch (err) {
      message.error('Unable to copy to clipboard');
    }
  }, []);

  const renderErrorPreview = useCallback((message?: string, details?: string) => {
    const combined = (message || details || '').trim();
    if (!combined) {
      return '—';
    }
    if (combined.length <= 160) {
      return combined;
    }
    return `${combined.slice(0, 160).trim()}…`;
  }, []);

  const renderResetSummary = useCallback((payload?: CharacterAnalysisResetBookResponse | null) => {
    if (!payload) {
      return <Text type="secondary">Run a dry run to preview impacted records.</Text>;
    }
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text>
          Support code: <Text code>{payload.supportCode || '—'}</Text>
        </Text>
        <Text>
          Chapters to clear: {payload.candidateCounts?.chaptersToClear ?? payload.chaptersClearedCount ?? 0}
        </Text>
        <Text>
          Analysis manifests to fail:{' '}
          {payload.candidateCounts?.analysisManifestsToFail ?? payload.analysisManifestsFailedCount ?? 0}
        </Text>
        <Text>
          Translation manifests to fail:{' '}
          {payload.candidateCounts?.translationManifestsToFail ?? payload.translationManifestsFailedCount ?? 0}
        </Text>
        <Text>
          Locks to expire: {payload.candidateCounts?.locksToExpire ?? payload.locksExpiredCount ?? 0}
        </Text>
        {!payload.dryRun && (
          <>
            <Text>Chapters cleared: {payload.chaptersClearedCount}</Text>
            <Text>Registry docs deleted: {payload.registryDocsDeleted}</Text>
            <Text>Character records deleted: {payload.characterRecordsDeleted}</Text>
            <Text>
              Relationship overrides deleted: {payload.relationshipReviewOverridesDeleted}
            </Text>
            <Text>Locks failed via manifest linkage: {payload.locksFailedCount}</Text>
            <Text>Locks expired in cleanup: {payload.locksExpiredCount}</Text>
          </>
        )}
        {(payload.warnings || []).length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`Warnings: ${(payload.warnings || []).slice(0, 2).join(' | ')}`}
          />
        )}
      </Space>
    );
  }, []);

  const columns = useMemo<ColumnsType<LockInspectorLock>>(
    () => [
      {
        title: 'Lock',
        dataIndex: 'lockGuid',
        key: 'lock',
        width: 220,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text code style={{ fontSize: 13 }}>{record.lockGuid || '—'}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.type || '—'} · {record.status || 'unknown'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Doc: {record.documentId || '—'}
            </Text>
            {record.useBatch ? (
              <Tooltip title="Lock created by a queued/batch translation request.">
                <Tag color="cyan" style={{ width: 'fit-content' }}>
                  Queued translation
                </Tag>
              </Tooltip>
            ) : null}
            {record.cleared && (
              <Tag color="geekblue" style={{ width: 'fit-content' }}>
                Cleared
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 210,
        render: (_, record) => {
          const statusKey = (record.statusNormalized || '').toLowerCase();
          const color = statusColorMap[statusKey] || 'default';
          return (
            <Space direction="vertical" size={0}>
              <Tag color={color} style={{ width: 'fit-content' }}>
                {toSentence(record.status)}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.isActive
                  ? `Active ${formatDuration(record.activeDurationSeconds ?? record.ageSeconds)}`
                  : `Elapsed ${formatDuration(record.durationSeconds)}`}
              </Text>
              {record.lastUpdateTime && (
                <Tooltip title={formatLockDateTime(record.lastUpdateTime)}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Updated {formatAgeLabel(record.lastUpdateAgeSeconds)}
                  </Text>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
      {
        title: 'Timing',
        dataIndex: 'timestamp',
        key: 'timing',
        width: 260,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text>
              Started:{' '}
              <Text type="secondary">{formatLockDateTime(record.timestamp as string)}</Text>
            </Text>
            {record.completionTime && (
              <Text>
                Completed:{' '}
                <Text type="secondary">{formatLockDateTime(record.completionTime as string)}</Text>
              </Text>
            )}
            {record.failureTime && (
              <Text>
                Failed:{' '}
                <Text type="secondary">{formatLockDateTime(record.failureTime as string)}</Text>
              </Text>
            )}
            {record.expiresAt && (
              <Text>
                Expires:{' '}
                <Text type="secondary">{formatLockDateTime(record.expiresAt as string)}</Text>
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: 'Scope',
        dataIndex: 'bookId',
        key: 'scope',
        width: 260,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <div>
              <Text strong>{record.bookTitle || record.bookId || '—'}</Text>
            </div>
            {record.bookId && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Book ID: {record.bookId}
              </Text>
            )}
            {record.chapterGuid && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Chapter: {record.chapterName || '—'} · {record.chapterGuid}
              </Text>
            )}
            {record.userId && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                User: {record.userId}
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: 'Language & Credits',
        key: 'language',
        width: 220,
        render: (_, record) => (
          <Space direction="vertical" size={0}>
            <Text type="secondary">
              {record.sourceLang || '—'} → {record.targetLang || '—'}
            </Text>
            {typeof record.word_count === 'number' && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Words: {record.word_count.toLocaleString()}
              </Text>
            )}
            {record.creditHoldAmount !== undefined && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Hold: {record.creditHoldAmount}
              </Text>
            )}
            {record.creditChargeStatus && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Charge: {toSentence(record.creditChargeStatus as string)}
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: 'Errors',
        dataIndex: 'error',
        key: 'errors',
        render: (_, record) => {
          const hasError = !!(record.error || record.errorDetails);
          if (!hasError) {
            return <Text type="secondary">—</Text>;
          }
          const preview = renderErrorPreview(record.error as string, record.errorDetails as string);
          return (
            <Space direction="vertical" size={4}>
              <Text type="danger" style={{ fontSize: 13 }}>
                {preview}
              </Text>
              <Button size="small" onClick={() => setErrorModalLock(record)}>
                View error details
              </Button>
            </Space>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 140,
        render: (_, record) => {
          if (!record.isActive) {
            return <Text type="secondary">—</Text>;
          }
          return (
            <Button danger size="small" onClick={() => openExpireModal(record)}>
              Expire lock
            </Button>
          );
        },
      },
    ],
    [openExpireModal, renderErrorPreview]
  );

  const summaryDetails = formatFilters(summary);

  return (
    <Card title="Lock Inspector">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12} lg={6}>
              <Input
                placeholder="User ID"
                value={userId}
                allowClear
                onChange={(e) => setUserId(e.target.value)}
              />
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Input
                placeholder="Book ID"
                value={bookId}
                allowClear
                onChange={(e) => setBookId(e.target.value)}
              />
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Input
                placeholder="Chapter GUID"
                value={chapterGuid}
                allowClear
                onChange={(e) => setChapterGuid(e.target.value)}
              />
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Input
                placeholder="Lock Support Code"
                value={lockGuid}
                allowClear
                onChange={(e) => setLockGuid(e.target.value)}
              />
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} md={12} lg={8}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Filter by status"
                value={statusFilters}
                onChange={(values) => setStatusFilters(values)}
                style={{ width: '100%' }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <Option key={status} value={status}>
                    {toSentence(status)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Select
                mode="multiple"
                allowClear
                placeholder="Filter by type"
                value={typeFilters}
                onChange={(values) => setTypeFilters(values)}
                style={{ width: '100%' }}
              >
                {TYPE_OPTIONS.map((type) => (
                  <Option key={type} value={type}>
                    {toSentence(type)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <InputNumber
                min={5}
                max={500}
                value={limit}
                onChange={(value) => setLimit(Number(value) || 50)}
                style={{ width: '100%' }}
                addonBefore="Limit"
              />
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Select value={sortOrder} onChange={(value) => setSortOrder(value)} style={{ width: '100%' }}>
                <Option value="newest">Newest first</Option>
                <Option value="oldest">Oldest first</Option>
              </Select>
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
            <Col xs={24} md={12} lg={6}>
              <Space>
                <Switch checked={activeOnly} onChange={setActiveOnly} />
                <span>Active locks only</span>
              </Space>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Space>
                <Switch checked={errorsOnly} onChange={setErrorsOnly} />
                <span>Only show locks with errors</span>
              </Space>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <InputNumber
                min={0}
                max={10080}
                value={inconsistentMinAgeMinutes}
                onChange={(value) => setInconsistentMinAgeMinutes(Number(value) || 0)}
                style={{ width: '100%' }}
                addonBefore="Min age (min)"
              />
            </Col>
            <Col xs={24} md={24} lg={24}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space wrap size={[8, 8]}>
                  <Button onClick={resetForm} disabled={loading}>
                    Reset
                  </Button>
                  <Button type="primary" onClick={inspectLocks} loading={loading} disabled={!hasIdentifier}>
                    Inspect
                  </Button>
                  <Button onClick={inspectInconsistentLocks} loading={loading}>
                    Find inconsistent
                  </Button>
                  <Button onClick={inspectAnalysisBlockedLocks} loading={loading}>
                    Find analysis-blocked locks
                  </Button>
                  <Button onClick={inspectAnalysisBlockedBooks} loading={loading}>
                    Find stuck books
                  </Button>
                  <Button danger onClick={openInconsistentBulkModal} disabled={!canRepairInconsistent}>
                    Repair inconsistent
                  </Button>
                  <Button danger onClick={openAnalysisBlockedBulkModal} disabled={!canExpireAnalysisBlocked}>
                    Expire analysis blocked
                  </Button>
                  <Button danger onClick={openResetBookModal} disabled={loading || !bookId.trim()}>
                    Recover Stuck Book
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </div>

        {responseMeta && (
          <div>
            <Text type="secondary">
              Last inspected {formatLockDateTime(responseMeta.inspectedAt)} ({responseMeta.queryLabel}) · Matches{' '}
              {humanizeCount(responseMeta.totalMatched)}
              {responseMeta.truncated ? ' (truncated to limit)' : ''}
            </Text>
            <br />
            <Text type="secondary">
              Showing {humanizeCount(filteredLocks.length)} of {humanizeCount(locks.length)} loaded locks after local
              filters.
            </Text>
          </div>
        )}

        {summary ? (
          <Card type="inner" size="small" title="Summary">
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <div>
                  <Text strong>Total</Text>
                  <div>{humanizeCount(summary.count)}</div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div>
                  <Text strong>Active</Text>
                  <div>{humanizeCount(summary.activeCount)}</div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div>
                  <Text strong>With Errors</Text>
                  <div>{humanizeCount(summary.errorCount)}</div>
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div>
                  <Text strong>Avg Duration</Text>
                  <div>{formatDuration(summary.averageDurationSeconds)}</div>
                </div>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col xs={24} md={8}>
                <Text strong>Max Duration:</Text> {formatDuration(summary.maxDurationSeconds)}
              </Col>
              <Col xs={24} md={8}>
                <Text strong>Longest Active:</Text> {formatDuration(summary.longestActiveSeconds)}
              </Col>
              <Col xs={24} md={8}>
                <Text strong>Range:</Text>{' '}
                {summary.timeRangeStart
                  ? `${formatLockDateTime(summary.timeRangeStart)} – ${formatLockDateTime(
                      summary.timeRangeEnd ?? undefined
                    )}`
                  : '—'}
              </Col>
            </Row>
            {summaryDetails && (
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col xs={24} md={12}>
                  <Text strong>Status Counts</Text>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {summaryDetails.statuses.length ? summaryDetails.statuses.join(' · ') : '—'}
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <Text strong>Type Counts</Text>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {summaryDetails.types.length ? summaryDetails.types.join(' · ') : '—'}
                  </div>
                </Col>
              </Row>
            )}
            {locks.length !== filteredLocks.length && (
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col span={24}>
                  <Alert
                    type="info"
                    showIcon
                    message={`Local filters applied: showing ${humanizeCount(filteredLocks.length)} of ${humanizeCount(
                      locks.length
                    )} locks.`}
                  />
                </Col>
              </Row>
            )}
          </Card>
        ) : (
          responseMeta && locks.length === 0 && (
            <Alert type="info" message="No locks matched the selected filters." showIcon />
          )
        )}

        {analysisBlockedBookIds.length > 0 && (
          <Card type="inner" size="small" title={`Stuck Books (${analysisBlockedBookIds.length})`}>
            <Text type="secondary">
              Books in this list currently have analysis-blocked manifests. Use one of these Book IDs with
              "Recover Stuck Book" to clear stale analysis state.
            </Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap size={[8, 8]}>
                {analysisBlockedBooks.map((entry) => (
                  <Tag key={entry.bookId} color="gold">
                    {entry.bookId} ({entry.blockedManifestCount})
                  </Tag>
                ))}
              </Space>
            </div>
          </Card>
        )}

        <Table<LockInspectorLock>
          dataSource={filteredLocks}
          columns={columns}
          rowKey={(record) =>
            record.documentId ||
            record.lockGuid ||
            `${record.bookId || 'book'}-${record.chapterGuid || 'chapter'}-${record.timestamp || 'ts'}`
          }
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={false}
          size="small"
        />
        <Modal
          title="Lock Error Details"
          visible={!!errorModalLock}
          onCancel={closeErrorModal}
          footer={[
            <Button key="close" onClick={closeErrorModal}>
              Close
            </Button>,
          ]}
          width={720}
        >
          {errorModalLock && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>Support Code:</Text>{' '}
                <Text code>{errorModalLock.lockGuid || '—'}</Text>
              </div>
              <div>
                <Text strong>Status:</Text> {toSentence(errorModalLock.status)}
              </div>
              {errorModalLock.error && (
                <div>
                  <Text strong>Friendly Error</Text>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, marginTop: 4 }}>
                    {errorModalLock.error}
                  </pre>
                </div>
              )}
              {errorModalLock.errorDetails && (
                <div>
                  <Text strong>Provider / Internal Details</Text>
                  <div
                    style={{
                      marginTop: 4,
                      padding: 12,
                      background: token.colorFillTertiary,
                      border: `1px solid ${token.colorSplit}`,
                      borderRadius: 4,
                      maxHeight: 240,
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, margin: 0 }}>
                      {errorModalLock.errorDetails}
                    </pre>
                  </div>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => copyToClipboard(String(errorModalLock.errorDetails))}
                  >
                    Copy details
                  </Button>
                </div>
              )}
              {(errorModalLock.expiredByAdminId || errorModalLock.expiredAt) && (
                <div>
                  <Text strong>Manual Expiry Metadata</Text>
                  <div style={{ fontSize: 13, marginTop: 4 }}>
                    {errorModalLock.expiredByAdminId && (
                      <div>Expired by: {errorModalLock.expiredByAdminId}</div>
                    )}
                    {errorModalLock.expiredAt && (
                      <div>Expired at: {formatLockDateTime(errorModalLock.expiredAt as string)}</div>
                    )}
                    {errorModalLock.expiredReason && (
                      <div>Reason: {errorModalLock.expiredReason}</div>
                    )}
                  </div>
                </div>
              )}
              {!errorModalLock.error && !errorModalLock.errorDetails && (
                <Alert type="info" message="No error details provided for this lock." showIcon />
              )}
            </Space>
          )}
        </Modal>
        <Modal
          title="Expire Lock"
          visible={!!expireModalLock}
          onCancel={closeExpireModal}
          onOk={handleExpireLock}
          confirmLoading={expireLoading}
          okButtonProps={{ danger: true }}
          okText="Expire lock"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This will mark the lock as expired, release any credit hold, and refund charges."
            />
            <div>
              <Text strong>Support Code:</Text> <Text code>{expireModalLock?.lockGuid || '—'}</Text>
            </div>
            <Input.TextArea
              rows={4}
              value={expireReason}
              onChange={(e) => setExpireReason(e.target.value)}
              placeholder="Reason shown to the user (optional)"
            />
          </Space>
        </Modal>
        <Modal
          title="Repair Inconsistent Locks"
          visible={inconsistentBulkModalVisible}
          onCancel={closeInconsistentBulkModal}
          onOk={handleRepairInconsistent}
          confirmLoading={inconsistentBulkLoading}
          okButtonProps={{ danger: true }}
          okText="Repair matched locks"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This bulk action fixes locks where status is terminal but processingState still looks active."
            />
            <InputNumber
              min={1}
              max={1000}
              value={inconsistentBulkMaxLocks}
              onChange={(value) => setInconsistentBulkMaxLocks(Number(value) || 1)}
              style={{ width: '100%' }}
              addonBefore="Max locks"
            />
            <Input.TextArea
              rows={4}
              value={inconsistentBulkReason}
              onChange={(e) => setInconsistentBulkReason(e.target.value)}
              placeholder="Repair reason (optional)"
            />
          </Space>
        </Modal>
        <Modal
          title="Expire Analysis-Blocked Locks"
          visible={analysisBlockedBulkModalVisible}
          onCancel={closeAnalysisBlockedBulkModal}
          onOk={handleExpireAnalysisBlocked}
          confirmLoading={analysisBlockedBulkLoading}
          okButtonProps={{ danger: true }}
          okText="Expire matched locks"
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This bulk action expires locks blocked by character-analysis state and releases/refunds credits when applicable."
            />
            <InputNumber
              min={1}
              max={1000}
              value={analysisBlockedBulkMaxLocks}
              onChange={(value) => setAnalysisBlockedBulkMaxLocks(Number(value) || 1)}
              style={{ width: '100%' }}
              addonBefore="Max locks"
            />
            <Input.TextArea
              rows={4}
              value={analysisBlockedBulkReason}
              onChange={(e) => setAnalysisBlockedBulkReason(e.target.value)}
              placeholder="Expire reason (optional)"
            />
          </Space>
        </Modal>
        <Modal
          title="Recover Stuck Book"
          visible={resetBookModalVisible}
          onCancel={closeResetBookModal}
          onOk={executeResetBook}
          confirmLoading={resetBookExecuteLoading}
          okButtonProps={{ danger: true, disabled: resetBookDryRunLoading }}
          okText="Execute reset"
          width={760}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="This admin recovery action hard-resets failed character-analysis state, fails active linked manifests, and expires related non-terminal locks."
            />
            <div>
              <Text strong>Book ID:</Text> <Text code>{bookId.trim() || '—'}</Text>
            </div>
            <Input.TextArea
              rows={3}
              value={resetBookReason}
              onChange={(e) => setResetBookReason(e.target.value)}
              placeholder="Reason for audit/support logs"
            />
            <Space>
              <Switch checked={resetBookForce} onChange={setResetBookForce} />
              <Text>Force reset even if analysis appears fresh/running</Text>
            </Space>
            <Space>
              <Switch checked={resetBookFreeRunFlag} onChange={setResetBookFreeRunFlag} />
              <Text>Also reset free-run flag</Text>
            </Space>
            <Space>
              <Button onClick={runResetBookDryRun} loading={resetBookDryRunLoading}>
                Refresh dry run
              </Button>
              <Text type="secondary">
                Dry run runs automatically when opening this dialog.
              </Text>
            </Space>
            <Card type="inner" size="small" title={resetBookResult ? 'Recovery Result' : 'Recovery Preview'}>
              {renderResetSummary(resetBookResult || resetBookPreview)}
            </Card>
          </Space>
        </Modal>
      </Space>
    </Card>
  );
};

export default LockInspector;
